import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence } from 'framer-motion';
import SearchOverlay from './SearchOverlay';
import RadialMenu from './RadialMenu';
import './AdminMenu.css';

const SEARCH_ICON = 'https://cdn-icons-png.flaticon.com/512/639/639426.png';
const CLOSE_ICON = 'data:image/svg+xml,' + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="22" height="22"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" fill="%230b2f3c" opacity="0.8"/></svg>');

const CG_MODELS = [
  { path: '/heart', title: 'Heart', sub: 'Interactive 3D & AR Experience', icon: 'https://cdn-icons-png.flaticon.com/512/954/954528.png', delay: 'delay-3' },
  { path: '/skeleton', title: 'Skeleton', sub: 'Complete Virtual Bone Structure', icon: 'https://cdn-icons-png.flaticon.com/512/7437/7437376.png', delay: 'delay-4' },
  { path: '/brain', title: 'Brain', sub: 'Explore the Neural Network', icon: 'https://cdn-icons-png.flaticon.com/512/6969/6969710.png', delay: 'delay-5' },
  { path: '/muscles', title: 'Muscles', sub: 'Muscular System Anatomy', icon: 'https://cdn-icons-png.flaticon.com/512/7671/7671649.png', delay: 'delay-6' },
  { path: '/cardiovascularsystem', title: 'Cardiovascular System', sub: 'Cardiovascular Anatomy', icon: 'https://cdn-icons-png.flaticon.com/512/14323/14323111.png', delay: 'delay-7' },
  { path: '/nervoussystem', title: 'Nervous System', sub: 'Neural Pathways & Nerves', icon: 'https://cdn-icons-png.flaticon.com/512/11603/11603901.png', delay: 'delay-8' },
  { path: '/lymphaticsystem', title: 'Lymphatic System', sub: 'Immune Network Anatomy', icon: 'https://cdn-icons-png.flaticon.com/512/3323/3323127.png', delay: 'delay-9' },
  { path: '/digestivesystem', title: 'Digestive System', sub: 'Digestive Tract Anatomy', icon: 'https://cdn-icons-png.flaticon.com/512/10172/10172608.png', delay: 'delay-11' },
  { path: '/respiratorysystem', title: 'Respiratory System', sub: 'Lungs & Respiratory Tract', icon: 'https://cdn-icons-png.flaticon.com/512/9774/9774039.png', delay: 'delay-12' },
  { path: '/urinarysystem', title: 'Urinary System', sub: 'Renal & Urinary Anatomy', icon: 'https://cdn-icons-png.flaticon.com/512/17306/17306383.png', delay: 'delay-13' },
];

const REAL_MODELS = [
  { path: '/neck', title: 'Neck', sub: '3D Cervical Spine from MRI Scan', icon: 'https://cdn-icons-png.flaticon.com/512/4533/4533534.png', delay: 'delay-6' },
  { path: '/skullchest', title: 'Skull & Chest', sub: '3D Upper Body from MRI Scan', icon: 'https://cdn-icons-png.flaticon.com/512/10299/10299270.png', delay: 'delay-7' },
  { path: '/chest', title: 'Chest', sub: '3D Thoracic Cavity from MRI Scan', icon: 'https://cdn-icons-png.flaticon.com/512/7220/7220470.png', delay: 'delay-8' },
];

function AdminMenu() {
  const menuRef = useRef(null);
  const searchInputRef = useRef(null);
  const searchWrapperRef = useRef(null);
  const blurTimeoutRef = useRef(null);
  const navigate = useNavigate();

  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [pillRect, setPillRect] = useState(null);

  const openSearch = useCallback(() => {
    setSearchOpen(true);
    setTimeout(() => searchInputRef.current?.focus(), 200);
  }, []);

  const closeSearch = useCallback(() => {
    setSearchOpen(false);
    setSearchQuery('');
    setPillRect(null);
  }, []);

  const handleIconClick = useCallback(() => {
    if (searchQuery.trim()) {
      setSearchQuery('');
      setTimeout(() => searchInputRef.current?.focus(), 0);
    } else if (searchOpen) {
      closeSearch();
    } else {
      openSearch();
    }
  }, [searchQuery, searchOpen, closeSearch, openSearch]);

  const handleSearchSelect = useCallback((systemId, meshKey) => {
    navigate(`/${systemId}`, { state: { focusMesh: meshKey } });
  }, [navigate]);

  useEffect(() => {
    if (menuRef.current) {
      menuRef.current.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, []);

  useEffect(() => {
    if (!searchOpen) return;
    const onKey = (e) => { if (e.key === 'Escape') closeSearch(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [searchOpen, closeSearch]);

  useEffect(() => {
    if (!searchOpen) {
      setPillRect(null);
      return;
    }
    const timer = setTimeout(() => {
      if (searchWrapperRef.current) {
        const r = searchWrapperRef.current.getBoundingClientRect();
        setPillRect({ left: r.left, bottom: r.bottom, width: r.width });
      }
    }, 420);
    return () => clearTimeout(timer);
  }, [searchOpen]);

  const handleBlur = useCallback(() => {
    blurTimeoutRef.current = setTimeout(() => {
      if (!searchQuery.trim()) closeSearch();
    }, 180);
  }, [searchQuery, closeSearch]);

  const handleFocus = useCallback(() => {
    if (blurTimeoutRef.current) clearTimeout(blurTimeoutRef.current);
  }, []);

  const handleInputKeyDown = useCallback((e) => {
    if (e.key === 'Escape') {
      closeSearch();
    } else if (e.key === 'Enter') {
      if (searchQuery.trim()) {
        searchInputRef.current?.blur();
      }
    }
  }, [closeSearch, searchQuery]);

  const [currentTime, setCurrentTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const timeString = currentTime.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  const dateString = currentTime.toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric' });
  const handleCardClick = (path) => {

    navigate(path);
  };

  const renderCard = (c) => (
    <div key={c.path} className={`card slide-up ${c.delay}`} onClick={() => handleCardClick(c.path)}>
      <div className="icon">
        <img src={c.icon} alt={c.title} />
      </div>
      <div className="card-title">{c.title}</div>
      <div className="card-sub">{c.sub}</div>
    </div>
  );

  return (
    <div className="admin-menu" ref={menuRef}>
      <div className="animated-gradient-bg" />

      <div className="admin-waves">
        <div className="admin-wave admin-wave-top"></div>
        <div className="admin-wave admin-wave-middle"></div>
        <div className="admin-wave admin-wave-bottom"></div>
      </div>

      {searchOpen && (
        <div className="search-pill is-open" ref={searchWrapperRef}>
          <input
            ref={searchInputRef}
            type="text"
            className="search-input"
            placeholder="Search anatomy models..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onBlur={handleBlur}
            onFocus={handleFocus}
            onKeyDown={handleInputKeyDown}
          />
          <button
            type="button"
            className="search-icon-btn"
            onMouseDown={(e) => e.preventDefault()}
            onClick={handleIconClick}
            title={searchQuery.trim() ? 'Clear' : 'Close'}
            aria-label={searchQuery.trim() ? 'Clear search' : 'Close search'}
          >
            {searchQuery.trim() ? (
              <img src={CLOSE_ICON} alt="" className="search-icon-img" />
            ) : (
              <img src={SEARCH_ICON} alt="" className="search-icon-img" />
            )}
          </button>
        </div>
      )}

      <div className="menu-content">
        <img src="/DarkLogo.png" alt="METATIP emblem" className="organs-logo" />

        <div className="page-title slide-up delay-1">Organs</div>
        <div className="subtitle slide-up delay-2">Discover human anatomy in 3D &amp; AR</div>

        <div className="section-title slide-up delay-3">Computer-Generated Models</div>
        <div className="section-sub slide-up delay-3">High-fidelity anatomical structures created digitally</div>
        <div className="grid">
          {CG_MODELS.map(renderCard)}
        </div>

        <div className="section-title slide-up delay-5">Real Anatomy Models</div>
        <div className="section-sub slide-up delay-5">MRI &amp; CT scan data transformed into true-to-life 3D</div>
        <div className="grid">
          {REAL_MODELS.map(renderCard)}
        </div>
      </div>

      <RadialMenu onSearch={openSearch} />

      <AnimatePresence>
        {searchOpen && (
          <SearchOverlay
            query={searchQuery}
            onSelect={handleSearchSelect}
            onClose={closeSearch}
            anchorRect={pillRect}
          />
        )}
      </AnimatePresence>
    </div>
  );
}

export default AdminMenu;
